class CollectionSection {
  constructor() {
    this.section = document.querySelector('[data-collection-section]');
    if (!this.section) return;

    this.sectionId = this.section.dataset.sectionId;
    this.mobileFilterDrawer = document.querySelector('[data-mobile-filter-drawer]');
    this.mobileSortDrawer = document.querySelector('[data-mobile-sort-drawer]');
    this.stickyBar = document.querySelector('[data-sticky-bar]');
    this.abortController = null;
    this.boundPriceSliders = new WeakSet();
    this.boundSortDropdowns = new WeakSet();
    this.boundLoadMoreButtons = new WeakSet();
    this.forceCloseAccordionId = null;

    this.bindSort();
    this.bindFilters();
    this.bindPriceSliders();
    this.bindMobileFilterDrawer();
    this.bindMobileSortDrawer();
    this.bindActiveFilterLinks();
    this.bindStickyBar();
    this.bindFiltersSidebarStuck();
    this.bindInfiniteScroll();
    this.correctInitialState();
  }

  // Bundles every "URL doesn't yet reflect what the UI implies" fix into one
  // corrected URL, or null when the given state already matches. sectionData
  // is whichever section element's dataset actually describes that state —
  // fetchAndRender() re-runs this against each fresh response's own section
  // (see there for why).
  buildCorrectedUrl(url, sectionData) {
    const correctedUrl = new URL(url);
    let needsCorrection = false;

    // The section falls back to displaying sort_already_applied's sort_key as
    // selected whenever the collection's native default isn't one of the
    // three supported keys — but collection.products inside {% paginate %}
    // is only the current page's slice, so re-sorting it in Liquid only
    // reorders each page internally instead of the whole collection.
    // Silently correcting the URL to an explicit sort_by makes Shopify's own
    // query do the real, cross-page sort, matching what the dropdown already
    // claims is selected.
    if (sectionData.sortNeedsCorrection === 'true' && !correctedUrl.searchParams.has('sort_by')) {
      correctedUrl.searchParams.set('sort_by', sectionData.defaultSort);
      needsCorrection = true;
    }

    return needsCorrection ? correctedUrl : null;
  }

  correctInitialState() {
    const correctedUrl = this.buildCorrectedUrl(window.location.href, this.section.dataset);
    if (correctedUrl) this.fetchAndRender(correctedUrl, { replace: true });
  }

  bindSort() {
    const sortDropdown = this.section.querySelector('#collection-sort');
    if (!sortDropdown) return;

    // renderSection() calls bindSort() again after every filter/sort change,
    // but the toolbar morph now preserves this element's identity (matched by
    // its stable id) instead of replacing it — so without this guard, each
    // render stacked another capture-phase click listener and another
    // popover:select listener on the same node, compounding on every use.
    if (this.boundSortDropdowns.has(sortDropdown)) return;
    this.boundSortDropdowns.add(sortDropdown);

    // On mobile, intercept the trigger before popover-dropdown's own click
    // handler opens its floating listbox — open the bottom-sheet drawer
    // instead. Capture phase on this ancestor always runs before the
    // trigger's own listener, so stopPropagation() here prevents the
    // listbox from ever opening. Desktop is untouched (matchMedia bails).
    sortDropdown.addEventListener(
      'click',
      (e) => {
        if (window.matchMedia('(min-width: 1024px)').matches) return;
        if (!e.target.closest('[data-popover-trigger]')) return;

        e.stopPropagation();
        this.openMobileSortDrawer();
      },
      { capture: true },
    );

    sortDropdown.addEventListener('popover:select', (e) => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', e.detail.value);
      url.searchParams.delete('page');
      this.fetchAndRender(url);
    });
  }

  bindFilters() {
    document.addEventListener('change', (e) => {
      const checkbox = e.target.closest('[data-filter-checkbox], [data-availability-toggle]');
      if (!checkbox) return;
      this.applyFilters();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.closest('[data-filter-price]')) {
        e.preventDefault();
        this.forceCloseAccordionId = this.getAccordionContentId(e.target);
        this.applyFilters();
      }
    });

    document.addEventListener('focusout', (e) => {
      if (e.target.closest('[data-filter-price]')) {
        this.forceCloseAccordionId = this.getAccordionContentId(e.target);
        this.applyFilters();
      }
    });
  }

  // Content id of the accordion group an element sits inside, if any —
  // matches the key captureAccordionStates()/restoreAccordionStates() use.
  getAccordionContentId(el) {
    const trigger = el.closest('[data-accordion]')?.querySelector('[data-accordion-trigger]');
    return trigger?.getAttribute('aria-controls') ?? null;
  }

  bindPriceSliders() {
    const sliders = document.querySelectorAll('[data-price-slider]');

    for (const slider of sliders) {
      const sliderMin = slider.querySelector('[data-slider-min]');
      const sliderMax = slider.querySelector('[data-slider-max]');
      const inputMin = slider.querySelector('[data-price-input-min]');
      const inputMax = slider.querySelector('[data-price-input-max]');
      const fill = slider.querySelector('[data-slider-fill]');

      if (!sliderMin || !sliderMax || !inputMin || !inputMax || !fill) continue;

      const rangeMax = parseFloat(slider.dataset.rangeMax) || 1;
      const minGap = this.getPriceMinGap(rangeMax);

      const updateFill = () => {
        const minPercent = (parseFloat(sliderMin.value) / rangeMax) * 100;
        const maxPercent = (parseFloat(sliderMax.value) / rangeMax) * 100;
        fill.style.setProperty('left', `${minPercent}%`);
        fill.style.setProperty('width', `${Math.max(0, maxPercent - minPercent)}%`);
      };

      // Must run on every render, before the bound-guard below: the server
      // markup carries no inline fill geometry, so a morph that preserves
      // these nodes strips the position set here and the fill goes blank.
      updateFill();

      if (this.boundPriceSliders.has(sliderMin)) continue;
      this.boundPriceSliders.add(sliderMin);

      sliderMin.addEventListener('input', () => {
        if (parseFloat(sliderMin.value) > parseFloat(sliderMax.value) - minGap) {
          sliderMin.value = parseFloat(sliderMax.value) - minGap;
        }
        inputMin.value = sliderMin.value;
        updateFill();
      });

      sliderMax.addEventListener('input', () => {
        if (parseFloat(sliderMax.value) < parseFloat(sliderMin.value) + minGap) {
          sliderMax.value = parseFloat(sliderMin.value) + minGap;
        }
        inputMax.value = sliderMax.value;
        updateFill();
      });

      sliderMin.addEventListener('change', () => {
        this.forceCloseAccordionId = this.getAccordionContentId(sliderMin);
        this.applyFilters();
      });

      sliderMax.addEventListener('change', () => {
        this.forceCloseAccordionId = this.getAccordionContentId(sliderMax);
        this.applyFilters();
      });

      inputMin.addEventListener('input', () => {
        const val = Math.min(parseFloat(inputMin.value) || 0, parseFloat(sliderMax.value) - minGap);
        sliderMin.value = val;
        updateFill();
      });

      inputMax.addEventListener('input', () => {
        const val = Math.max(parseFloat(inputMax.value) || 0, parseFloat(sliderMin.value) + minGap);
        sliderMax.value = val;
        updateFill();
      });
    }
  }

  // The server renders every filter group collapsed, so a re-render would
  // otherwise slam shut whatever the shopper had open. Keyed on the accordion's
  // stable content id, which survives the morph.
  captureAccordionStates(root) {
    const states = new Map();
    if (!root) return states;

    for (const trigger of root.querySelectorAll('[data-accordion-trigger]')) {
      const contentId = trigger.getAttribute('aria-controls');
      if (!contentId) continue;
      states.set(contentId, trigger.getAttribute('aria-expanded') === 'true');
    }
    return states;
  }

  // Replays the difference as a real click so the accordion's own handler owns
  // the height/icon/focusability updates rather than duplicating them here.
  restoreAccordionStates(root, states) {
    if (!root || states.size === 0) return;

    for (const trigger of root.querySelectorAll('[data-accordion-trigger]')) {
      const contentId = trigger.getAttribute('aria-controls');
      if (!contentId || !states.has(contentId)) continue;

      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      if (states.get(contentId) !== isOpen) trigger.click();
    }
  }

  // Minimum distance the two thumbs must keep apart. A flat 100 goes negative
  // on collections whose whole price range is under 100, which pins both thumbs
  // and makes the slider undraggable — scale it down for narrow ranges.
  getPriceMinGap(rangeMax) {
    return Math.max(1, Math.min(100, Math.floor(rangeMax / 10)));
  }

  // Availability is an "in stock only" toggle. Unchecking it clears the
  // filter entirely (show every product) rather than switching to an
  // out-of-stock-only filter.
  appendAvailabilityParam(url, source) {
    const toggle = source.querySelector('[data-availability-toggle], [data-drawer-availability-toggle]');
    if (!toggle) return;

    if (toggle.checked) {
      const { inStockParam, inStockValue } = toggle.dataset;
      url.searchParams.append(inStockParam, inStockValue);
    }
  }

  applyFilters() {
    const url = new URL(window.location.href);

    const keysToDelete = [];
    url.searchParams.forEach((_, key) => {
      if (key.startsWith('filter.')) keysToDelete.push(key);
    });
    keysToDelete.forEach((key) => url.searchParams.delete(key));
    url.searchParams.delete('page');

    const sidebar = this.section.querySelector('[data-filters-sidebar]');
    const source = sidebar || document;

    const checkboxes = [...source.querySelectorAll('[data-filter-checkbox]:checked')];
    checkboxes.forEach((cb) => {
      url.searchParams.append(cb.name, cb.value);
    });
    this.appendAvailabilityParam(url, source);

    this.appendPriceParams(url, source.querySelectorAll('[data-filter-price]'));

    this.fetchAndRender(url);
  }

  // Both the min and max price inputs render pre-filled with the full
  // range (0 to the collection's max price) even when no price filter is
  // active — so blindly sending both on every filter change made unrelated
  // filters (e.g. Availability) "apply" an unchanged price range too. Only
  // send a price param once the shopper has actually narrowed past its
  // default bound.
  appendPriceParams(url, priceInputs) {
    priceInputs.forEach((input) => {
      if (!input.value) return;
      const price = parseFloat(input.value);
      if (isNaN(price) || price <= 0) return;

      const isMaxInput =
        input.hasAttribute('data-price-input-max') || input.hasAttribute('data-drawer-price-input-max');
      const bound = isMaxInput ? input.max : input.min;
      if (bound && parseFloat(bound) === price) return;

      url.searchParams.set(input.name, price);
    });
  }

  bindActiveFilterLinks() {
    document.addEventListener('click', (e) => {
      const filterLink = e.target.closest('[data-active-filter]');
      const clearLink = e.target.closest('[data-clear-filters]');

      if (filterLink) {
        e.preventDefault();
        this.fetchAndRender(new URL(filterLink.href));
      } else if (clearLink) {
        e.preventDefault();
        this.fetchAndRender(new URL(clearLink.href));
        this.closeMobileFilterDrawer();
      }
    });
  }

  async fetchAndRender(url, { replace = false } = {}) {
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    const grid = this.section.querySelector('[data-collection-products]');
    const loader = this.section.querySelector('[data-collection-loader]');
    if (grid) grid.classList.add('opacity-50', 'pointer-events-none');
    if (loader) {
      loader.classList.remove('hidden');
    }
    document.body.classList.add('overflow-hidden');

    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('sections', this.sectionId);

    try {
      const response = await fetch(fetchUrl.toString(), {
        signal: this.abortController.signal,
      });

      if (!response.ok) throw new Error('Failed to fetch collection');

      const data = await response.json();
      const html = data[this.sectionId];

      if (!html) throw new Error('No section HTML returned');

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newSection = doc.querySelector('[data-collection-section]');

      // The response just fetched is the authority on whether ITS state needs
      // correcting — e.g. a sort-by that isn't one of our three supported
      // keys. Checking this.section's dataset instead would use whatever
      // state the page started with, not what this particular response
      // actually came back as.
      const correctedUrl = newSection ? this.buildCorrectedUrl(url, newSection.dataset) : null;
      if (correctedUrl) {
        // Preserve the caller's original replace intent — e.g. "Clear all
        // filters" should still land as one new history entry (so Back
        // returns to the prior filtered view), just with the corrected URL
        // from the start instead of the raw, uncorrected one.
        await this.fetchAndRender(correctedUrl, { replace });
        return;
      }

      this.renderSection(doc, newSection);
      if (replace) {
        window.history.replaceState({}, '', url.toString());
      } else {
        window.history.pushState({}, '', url.toString());
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (grid) grid.classList.remove('opacity-50', 'pointer-events-none');
      if (loader) {
        loader.classList.add('hidden');
      }
      document.body.classList.remove('overflow-hidden');
    }
  }

  renderSection(doc, newSection) {
    const scrollY = window.scrollY;

    const newToolbar = newSection.querySelector('[data-collection-toolbar]');
    const currentToolbar = this.section.querySelector('[data-collection-toolbar]');
    if (newToolbar && currentToolbar) {
      // No morphStyle option: both sides are live attached elements of the
      // same tag, so the default "outerHTML" style reconciles currentToolbar
      // in place. Passing morphStyle:"innerHTML" here — the previous code —
      // makes Idiomorph treat newToolbar as a bare sibling marker rather than
      // a container, which clones the whole element as an extra nested child
      // instead of morphing into it. That silently doubled every fixed-size
      // wrapper's own padding (aside/product grid pt-10, etc.) on every
      // filter change, and left two elements sharing one id — which is what
      // broke sort-by rebinding after applying a filter.
      Idiomorph.morph(currentToolbar, newToolbar);
    }

    const newActiveFilters = newSection.querySelector('[data-active-filters-container]');
    const currentActiveFilters = this.section.querySelector('[data-active-filters-container]');
    if (newActiveFilters && currentActiveFilters) {
      // Plain innerHTML swap, not a morph: the chips carry no per-node state
      // (their clicks are delegated in bindActiveFilterLinks()), and clearing
      // filters needs this container to go fully empty — a direct swap
      // guarantees that instead of depending on morph diffing to fully
      // remove the last chip's wrapper, which was leaving its padding/margin
      // behind as a stray gap above the filter groups.
      currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
    }

    const newSidebar = newSection.querySelector('[data-filters-sidebar]');
    const currentSidebar = this.section.querySelector('[data-filters-sidebar]');
    let sidebarAccordions = null;
    if (newSidebar && currentSidebar) {
      sidebarAccordions = this.captureAccordionStates(currentSidebar);
      // Submitting a price change closes that group instead of preserving
      // whatever state it was in — every other group is left untouched.
      if (this.forceCloseAccordionId) {
        sidebarAccordions.set(this.forceCloseAccordionId, false);
      }
      Idiomorph.morph(currentSidebar, newSidebar);
    }
    this.forceCloseAccordionId = null;

    const newProducts = newSection.querySelector('[data-collection-products]');
    const currentProducts = this.section.querySelector('[data-collection-products]');
    if (newProducts && currentProducts) {
      Idiomorph.morph(currentProducts, newProducts);
      currentProducts.classList.remove('opacity-50', 'pointer-events-none');
    }
    // fetchAndRender() shows this before the request starts; only the error
    // path was hiding it again, so after any successful fetch it stayed
    // visible on top of the grid — blocking clicks on every product card —
    // for the rest of the session.
    this.section.querySelector('[data-collection-loader]')?.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');

    const newMobileDrawer = doc.querySelector('[data-mobile-filter-drawer]');
    if (newMobileDrawer && this.mobileFilterDrawer) {
      const newCount = newMobileDrawer.querySelector('[data-mobile-filter-count]');
      const currentCount = this.mobileFilterDrawer.querySelector('[data-mobile-filter-count]');
      if (newCount && currentCount) Idiomorph.morph(currentCount, newCount);

      const newBody = newMobileDrawer.querySelector('[data-mobile-filter-body]');
      const currentBody = this.mobileFilterDrawer.querySelector('[data-mobile-filter-body]');
      if (newBody && currentBody) {
        const drawerAccordions = this.captureAccordionStates(currentBody);
        Idiomorph.morph(currentBody, newBody);
        window.initAccordions?.();
        this.restoreAccordionStates(currentBody, drawerAccordions);
      }
    }

    window.scrollTo(0, scrollY);
    this.bindSort();
    this.bindPriceSliders();
    this.bindDrawerPriceSliders();
    this.bindInfiniteScroll();
    this.updateIndicatorDots();

    // Any group the shopper had expanded stays expanded. Runs after init so
    // newly-created triggers already have their click handler attached.
    if (sidebarAccordions) {
      window.initAccordions?.();
      this.restoreAccordionStates(currentSidebar, sidebarAccordions);
    }

    // Results just changed size — settle scroll-position-dependent chrome
    // against the new layout instead of waiting for the next scroll event.
    requestAnimationFrame(() => {
      this.updateSidebarStuck?.();
      this.refreshStickyBar?.();
    });
  }

  // --- Sticky Bar (IntersectionObserver) ---

  bindStickyBar() {
    if (!this.stickyBar) return;

    const toolbar = this.section.querySelector('[data-collection-toolbar]');
    const footer = document.querySelector('.footer');
    if (!toolbar) return;

    const LG_BREAKPOINT = 1024;

    // body flag hides the floating Rewards / Live Talk widgets, which sit over the bar (base.css)
    const showBar = () => {
      this.stickyBar.classList.remove('opacity-0', 'invisible', 'translate-y-4');
      this.stickyBar.classList.add('opacity-100', 'visible', 'translate-y-0');
      document.body.toggleAttribute('data-collection-bar-visible', true);
    };

    const hideBar = () => {
      this.stickyBar.classList.add('opacity-0', 'invisible', 'translate-y-4');
      this.stickyBar.classList.remove('opacity-100', 'visible', 'translate-y-0');
      document.body.toggleAttribute('data-collection-bar-visible', false);
    };

    // Show sticky bar only when toolbar is above viewport
    // AND footer is not yet in view
    let toolbarAbove = false;
    let footerVisible = false;

    const updateBar = () => {
      requestAnimationFrame(() => {
        if (toolbarAbove && !footerVisible) {
          showBar();
        } else {
          hideBar();
        }
      });
    };

    // IntersectionObserver only fires asynchronously, so after a filter change
    // resizes the page the bar can be left showing against stale geometry.
    // Re-read both rects directly so renderSection() can settle it immediately.
    this.refreshStickyBar = () => {
      if (window.innerWidth >= LG_BREAKPOINT) {
        hideBar();
        return;
      }
      const toolbarRect = toolbar.getBoundingClientRect();
      toolbarAbove = toolbarRect.bottom < 0;
      footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      updateBar();
    };

    const toolbarObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          toolbarAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        }
        updateBar();
      },
      { threshold: 0 },
    );

    const footerObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          footerVisible = entry.isIntersecting;
        }
        updateBar();
      },
      { threshold: 0 },
    );

    let isObserving = false;

    const attachObserver = () => {
      if (window.innerWidth < LG_BREAKPOINT && !isObserving) {
        toolbarObserver.observe(toolbar);
        if (footer) footerObserver.observe(footer);
        isObserving = true;
      } else if (window.innerWidth >= LG_BREAKPOINT && isObserving) {
        toolbarObserver.disconnect();
        footerObserver.disconnect();
        isObserving = false;
        hideBar();
      }
    };

    attachObserver();

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(attachObserver, 150);
    });
  }

  // --- Filters Sidebar Stuck State ---

  // IntersectionObserver callbacks are asynchronous and batched by the
  // browser — they can fire a beat before or after `position: sticky`
  // actually engages, so the padding swap looked out of sync with the real
  // stick point on real (non-programmatic) scrolling. Reading the sidebar's
  // own rendered position on every scroll frame ties the toggle to the exact
  // same layout the browser just used to render the sticky offset, so it can
  // never be early or late. This is safe from the earlier feedback-loop
  // concern too: only `top` is checked, and toggling `.is-stuck` changes the
  // sidebar's padding/height, not its top edge — height doesn't affect `top`.
  bindFiltersSidebarStuck() {
    const sidebar = this.section.querySelector('[data-filters-sidebar]');
    if (!sidebar) return;

    const stickyTop = Number.parseFloat(getComputedStyle(sidebar).top) || 0;

    let ticking = false;
    const updateStuck = () => {
      ticking = false;
      const isStuck = sidebar.getBoundingClientRect().top <= stickyTop;
      sidebar.classList.toggle('is-stuck', isStuck);
    };

    // Exposed so renderSection() can re-evaluate after a filter change resizes
    // the sidebar — otherwise stuck padding lingers once results shrink.
    this.updateSidebarStuck = updateStuck;

    updateStuck();
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(updateStuck);
      },
      { passive: true },
    );
  }

  // --- Indicator Dots ---

  updateIndicatorDots() {
    const url = new URL(window.location.href);

    // Check if any filter.* params are present
    let hasActiveFilters = false;
    url.searchParams.forEach((_, key) => {
      if (key.startsWith('filter.')) hasActiveFilters = true;
    });

    // Check if sort differs from default
    const defaultSort = this.stickyBar?.dataset.defaultSort ?? '';
    const currentSort = url.searchParams.get('sort_by') ?? '';
    const isSortActive = currentSort !== '' && currentSort !== defaultSort;

    // Update all filter indicator dots
    const filterDots = document.querySelectorAll('[data-filter-indicator]');
    for (const dot of filterDots) {
      if (hasActiveFilters) {
        dot.classList.remove('hidden');
      } else {
        dot.classList.add('hidden');
      }
    }

    // Update all sort indicator dots
    const sortDots = document.querySelectorAll('[data-sort-indicator]');
    for (const dot of sortDots) {
      if (isSortActive) {
        dot.classList.remove('hidden');
      } else {
        dot.classList.add('hidden');
      }
    }
  }

  // --- Mobile Filter Drawer (staged apply) ---

  bindMobileFilterDrawer() {
    if (!this.mobileFilterDrawer) return;

    this.bindDrawerPriceSliders();

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-filter-toggle]')) {
        const sidebar = this.section.querySelector('[data-filters-sidebar]');
        const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

        if (isDesktop && sidebar) {
          sidebar.scrollIntoView({ behavior: 'smooth', block: 'start' });
          sidebar.classList.add('ring-2', 'ring-foreground');
          window.setTimeout(() => {
            sidebar.classList.remove('ring-2', 'ring-foreground');
          }, 1000);
          return;
        }

        this.openMobileFilterDrawer();
      }
    });

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-mobile-filter-cancel]') || e.target.closest('[data-mobile-filter-overlay]')) {
        this.cancelMobileFilterDrawer();
      }
    });

    const applyButton = this.mobileFilterDrawer.querySelector('[data-mobile-filter-apply]');
    applyButton?.addEventListener('click', () => this.applyMobileFilterDrawer());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.mobileFilterDrawer.classList.contains('hidden')) {
        this.cancelMobileFilterDrawer();
      }
    });

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
        if (isDesktop && !this.mobileFilterDrawer.classList.contains('hidden')) {
          this.cancelMobileFilterDrawer();
        }
      }, 150);
    });
  }

  openMobileFilterDrawer() {
    const overlay = this.mobileFilterDrawer.querySelector('[data-mobile-filter-overlay]');
    const panel = this.mobileFilterDrawer.querySelector('[data-mobile-filter-panel]');
    const body = this.mobileFilterDrawer.querySelector('[data-mobile-filter-body]');

    this.mobileFilterBodySnapshot = body ? body.innerHTML : '';

    this.mobileFilterDrawer.classList.remove('hidden');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        overlay?.classList.remove('opacity-0');
        overlay?.classList.add('opacity-100');
        panel?.classList.remove('translate-y-full');
        panel?.classList.add('translate-y-0');
      });
    });
    document.body.classList.add('overflow-hidden');

    // Fix expanded accordions' content height now that the drawer is visible
    // (scrollHeight returns 0 inside hidden containers, so any group that
    // starts open needs its maxHeight recomputed once revealed). This only
    // touches inline height, not click bindings, so re-opening the drawer
    // never stacks duplicate listeners on the trigger.
    const drawerAccordions = this.mobileFilterDrawer.querySelectorAll('[data-accordion]');
    for (const accordion of drawerAccordions) {
      const trigger = accordion.querySelector('[data-accordion-trigger]');
      const content = accordion.querySelector('[data-accordion-content]');
      if (!trigger || !content) continue;
      if (trigger.getAttribute('aria-expanded') === 'true') {
        content.style.maxHeight = `${content.scrollHeight}px`;
      }
    }
  }

  closeMobileFilterDrawer() {
    if (!this.mobileFilterDrawer) return;

    const overlay = this.mobileFilterDrawer.querySelector('[data-mobile-filter-overlay]');
    const panel = this.mobileFilterDrawer.querySelector('[data-mobile-filter-panel]');

    overlay?.classList.remove('opacity-100');
    overlay?.classList.add('opacity-0');
    panel?.classList.remove('translate-y-0');
    panel?.classList.add('translate-y-full');

    setTimeout(() => {
      this.mobileFilterDrawer.classList.add('hidden');
      document.body.classList.remove('overflow-hidden');
    }, 300);
  }

  cancelMobileFilterDrawer() {
    const body = this.mobileFilterDrawer.querySelector('[data-mobile-filter-body]');
    if (body && this.mobileFilterBodySnapshot !== undefined) {
      body.innerHTML = this.mobileFilterBodySnapshot;
      this.bindDrawerPriceSliders();
    }
    this.closeMobileFilterDrawer();
  }

  applyMobileFilterDrawer() {
    const body = this.mobileFilterDrawer.querySelector('[data-mobile-filter-body]');
    if (!body) return;

    const url = new URL(window.location.href);
    const keysToDelete = [];
    url.searchParams.forEach((_, key) => {
      if (key.startsWith('filter.')) keysToDelete.push(key);
    });
    keysToDelete.forEach((key) => url.searchParams.delete(key));
    url.searchParams.delete('page');

    const checkboxes = body.querySelectorAll('[data-drawer-filter-checkbox]:checked');
    checkboxes.forEach((cb) => {
      url.searchParams.append(cb.name, cb.value);
    });
    this.appendAvailabilityParam(url, body);

    this.appendPriceParams(url, body.querySelectorAll('[data-drawer-filter-price]'));

    this.fetchAndRender(url);
    this.closeMobileFilterDrawer();
  }

  bindDrawerPriceSliders() {
    if (!this.mobileFilterDrawer) return;
    const sliders = this.mobileFilterDrawer.querySelectorAll('[data-drawer-price-slider]');

    for (const slider of sliders) {
      const sliderMin = slider.querySelector('[data-drawer-slider-min]');
      const sliderMax = slider.querySelector('[data-drawer-slider-max]');
      const inputMin = slider.querySelector('[data-drawer-price-input-min]');
      const inputMax = slider.querySelector('[data-drawer-price-input-max]');
      const fill = slider.querySelector('[data-slider-fill]');

      if (!sliderMin || !sliderMax || !inputMin || !inputMax || !fill) continue;

      const rangeMax = parseFloat(slider.dataset.rangeMax) || 1;
      const minGap = this.getPriceMinGap(rangeMax);

      const updateFill = () => {
        const minPercent = (parseFloat(sliderMin.value) / rangeMax) * 100;
        const maxPercent = (parseFloat(sliderMax.value) / rangeMax) * 100;
        fill.style.setProperty('left', `${minPercent}%`);
        fill.style.setProperty('width', `${Math.max(0, maxPercent - minPercent)}%`);
      };

      // See bindPriceSliders() — must run before the bound-guard on every pass.
      updateFill();

      if (this.boundPriceSliders.has(sliderMin)) continue;
      this.boundPriceSliders.add(sliderMin);

      sliderMin.addEventListener('input', () => {
        if (parseFloat(sliderMin.value) > parseFloat(sliderMax.value) - minGap) {
          sliderMin.value = parseFloat(sliderMax.value) - minGap;
        }
        inputMin.value = sliderMin.value;
        updateFill();
      });

      sliderMax.addEventListener('input', () => {
        if (parseFloat(sliderMax.value) < parseFloat(sliderMin.value) + minGap) {
          sliderMax.value = parseFloat(sliderMin.value) + minGap;
        }
        inputMax.value = sliderMax.value;
        updateFill();
      });

      inputMin.addEventListener('input', () => {
        const val = Math.min(parseFloat(inputMin.value) || 0, parseFloat(sliderMax.value) - minGap);
        sliderMin.value = val;
        updateFill();
      });

      inputMax.addEventListener('input', () => {
        const val = Math.max(parseFloat(inputMax.value) || 0, parseFloat(sliderMin.value) + minGap);
        sliderMax.value = val;
        updateFill();
      });
    }
  }

  // --- Mobile Sort Drawer (staged apply) ---

  bindMobileSortDrawer() {
    if (!this.mobileSortDrawer) return;

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-sort-toggle]')) {
        this.openMobileSortDrawer();
      }
    });

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-mobile-sort-cancel]') || e.target.closest('[data-mobile-sort-overlay]')) {
        this.cancelMobileSortDrawer();
      }
    });

    const applyButton = this.mobileSortDrawer.querySelector('[data-mobile-sort-apply]');
    applyButton?.addEventListener('click', () => this.applyMobileSortDrawer());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.mobileSortDrawer.classList.contains('hidden')) {
        this.cancelMobileSortDrawer();
      }
    });

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
        if (isDesktop && !this.mobileSortDrawer.classList.contains('hidden')) {
          this.cancelMobileSortDrawer();
        }
      }, 150);
    });
  }

  openMobileSortDrawer() {
    const url = new URL(window.location.href);
    const activeSort = url.searchParams.get('sort_by') || this.stickyBar?.dataset.defaultSort || '';

    const options = this.mobileSortDrawer.querySelectorAll('[data-mobile-sort-option]');
    for (const option of options) {
      option.checked = option.value === activeSort;
    }

    const overlay = this.mobileSortDrawer.querySelector('[data-mobile-sort-overlay]');
    const panel = this.mobileSortDrawer.querySelector('[data-mobile-sort-panel]');

    this.mobileSortDrawer.classList.remove('hidden');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        overlay?.classList.remove('opacity-0');
        overlay?.classList.add('opacity-100');
        panel?.classList.remove('translate-y-full');
        panel?.classList.add('translate-y-0');
      });
    });
    document.body.classList.add('overflow-hidden');
  }

  closeMobileSortDrawer() {
    if (!this.mobileSortDrawer) return;

    const overlay = this.mobileSortDrawer.querySelector('[data-mobile-sort-overlay]');
    const panel = this.mobileSortDrawer.querySelector('[data-mobile-sort-panel]');

    overlay?.classList.remove('opacity-100');
    overlay?.classList.add('opacity-0');
    panel?.classList.remove('translate-y-0');
    panel?.classList.add('translate-y-full');

    setTimeout(() => {
      this.mobileSortDrawer.classList.add('hidden');
      document.body.classList.remove('overflow-hidden');
    }, 300);
  }

  cancelMobileSortDrawer() {
    this.closeMobileSortDrawer();
  }

  applyMobileSortDrawer() {
    const checkedOption = this.mobileSortDrawer.querySelector('[data-mobile-sort-option]:checked');
    if (!checkedOption) return;

    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', checkedOption.value);
    url.searchParams.delete('page');

    this.fetchAndRender(url);
    this.closeMobileSortDrawer();
  }

  // --- Infinite Scroll / Load More ---

  // Re-run after every filter/sort render (renderSection() calls this again),
  // not just once at construction — otherwise nextPage/hasNextPage and the
  // observer/button stay pinned to whatever the very first, unfiltered page
  // load looked like, drifting out of sync with the actual filtered result
  // set on every later render. Always disconnect the previous observer first
  // since each render's sentinel is a different node.
  bindInfiniteScroll() {
    this.infiniteScrollObserver?.disconnect();

    const paginationInfo = this.section.querySelector('[data-pagination-info]');
    if (!paginationInfo) {
      this.hasNextPage = false;
      return;
    }

    this.paginationMode = paginationInfo.dataset.paginationMode;
    this.nextPage = parseInt(paginationInfo.dataset.currentPage, 10) + 1;
    this.hasNextPage = paginationInfo.dataset.hasNextPage === 'true';
    this.isFetchingNextPage = false;

    if (this.paginationMode === 'load_more') {
      const button = this.section.querySelector('[data-load-more-button]');
      if (button && !this.boundLoadMoreButtons.has(button)) {
        this.boundLoadMoreButtons.add(button);
        button.addEventListener('click', () => this.fetchNextPage());
      }
      return;
    }

    const sentinel = this.section.querySelector('[data-infinite-scroll-sentinel]');
    if (!sentinel) return;

    this.infiniteScrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) this.fetchNextPage();
      },
      { rootMargin: '400px' },
    );
    this.infiniteScrollObserver.observe(sentinel);
  }

  async fetchNextPage() {
    if (this.isFetchingNextPage || !this.hasNextPage) return;
    this.isFetchingNextPage = true;

    const loadMoreButton = this.section.querySelector('[data-load-more-button]');
    const loadMoreText = loadMoreButton?.querySelector('[data-button-text]');
    const loadMoreLoader = loadMoreButton?.querySelector('[data-button-loader]');
    const infiniteLoader = this.section.querySelector('[data-infinite-scroll-loader]');

    if (loadMoreButton) loadMoreButton.disabled = true;
    loadMoreText?.classList.add('invisible');
    loadMoreLoader?.classList.remove('hidden');
    infiniteLoader?.classList.remove('hidden');

    const url = new URL(window.location.href);
    url.searchParams.set('page', this.nextPage);
    url.searchParams.set('sections', this.sectionId);

    try {
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error('Failed to fetch next page');
      const data = await response.json();
      const html = data[this.sectionId];
      if (!html) throw new Error('No section HTML returned');

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('[data-product-grid]');
      const currentGrid = this.section.querySelector('[data-product-grid]');

      if (newGrid && currentGrid) {
        while (newGrid.firstChild) {
          currentGrid.appendChild(newGrid.firstChild);
        }
      }

      const newPaginationInfo = doc.querySelector('[data-pagination-info]');
      this.hasNextPage = newPaginationInfo?.dataset.hasNextPage === 'true';
      this.nextPage += 1;

      if (!this.hasNextPage) {
        this.infiniteScrollObserver?.disconnect();
        if (loadMoreButton) loadMoreButton.hidden = true;
      }
    } finally {
      this.isFetchingNextPage = false;
      loadMoreText?.classList.remove('invisible');
      loadMoreLoader?.classList.add('hidden');
      infiniteLoader?.classList.add('hidden');
      if (loadMoreButton && this.hasNextPage) loadMoreButton.disabled = false;
    }
  }
}

window.addEventListener('popstate', () => {
  window.location.reload();
});

document.addEventListener('DOMContentLoaded', () => {
  new CollectionSection();
});
